import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

const INK    = '#0d0d0d';
const BLUE   = '#1755f4';
const MUTED  = '#888';
const BORDER = '#e8e8e8';
const FONT   = "'Helvetica Neue', Helvetica, Arial, sans-serif";

const CATEGORIES = [
  { key: 'all',          label: 'Alle' },
  { key: 'maintenance',  label: 'Instandhaltung' },
  { key: 'admin',        label: 'Verwaltung' },
  { key: 'finance',      label: 'Finanzen' },
  { key: 'inspection',   label: 'Besichtigung' },
  { key: 'other',        label: 'Sonstiges' },
];

const PRIORITY_CFG = {
  low:    { label: 'Niedrig',  color: '#6b7280' },
  medium: { label: 'Mittel',   color: '#b45309' },
  high:   { label: 'Hoch',     color: '#b91c1c' },
};

const inputStyle = {
  width: '100%', border: `1px solid ${BORDER}`, borderRadius: 0,
  padding: '10px 12px', fontSize: '13px', boxSizing: 'border-box',
  fontFamily: FONT, color: INK, background: 'white', outline: 'none',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED, marginBottom: '8px' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const EMPTY = { title: '', category: 'other', priority: 'medium', due_date: '', notes: '' };

export default function Tasks() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();

  const [tasks, setTasks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [category, setCategory]   = useState('all');
  const [showDone, setShowDone]   = useState(false);
  const [modal, setModal]         = useState(null); // null | 'new' | task object (edit)
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(null);

  useEffect(() => { fetchTasks(); }, []);

  async function fetchTasks() {
    const { data } = await supabase
      .from('tasks')
      .select('*')
      .eq('owner_id', user.id)
      .order('due_date', { ascending: true, nullsLast: true });
    setTasks(data || []);
    setLoading(false);
  }

  const filtered = tasks.filter(t => {
    if (!showDone && t.done) return false;
    if (category !== 'all' && t.category !== category) return false;
    return true;
  });

  const openNew = () => {
    setForm({ ...EMPTY, due_date: new Date().toISOString().split('T')[0] });
    setModal('new');
  };

  const openEdit = (task) => {
    setForm({
      title:    task.title,
      category: task.category,
      priority: task.priority,
      due_date: task.due_date || '',
      notes:    task.notes || '',
    });
    setModal(task);
  };

  const closeModal = () => { setModal(null); setSaving(false); };

  async function handleSave(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);

    const payload = {
      owner_id: user.id,
      title:    form.title.trim(),
      category: form.category,
      priority: form.priority,
      due_date: form.due_date || null,
      notes:    form.notes.trim() || null,
    };

    if (modal === 'new') {
      const { error } = await supabase.from('tasks').insert({ ...payload, done: false });
      if (error) { addToast(error.message, 'error'); setSaving(false); return; }
      addToast('Aufgabe erstellt.', 'success');
    } else {
      const { error } = await supabase.from('tasks').update(payload).eq('id', modal.id);
      if (error) { addToast(error.message, 'error'); setSaving(false); return; }
      addToast('Aufgabe gespeichert.', 'success');
    }

    await fetchTasks();
    closeModal();
  }

  async function toggleDone(task) {
    await supabase.from('tasks').update({ done: !task.done }).eq('id', task.id);
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, done: !t.done } : t));
  }

  async function handleDelete(task) {
    setDeleting(task.id);
    await supabase.from('tasks').delete().eq('id', task.id);
    setTasks(prev => prev.filter(t => t.id !== task.id));
    setDeleting(null);
    addToast('Aufgabe gelöscht.', 'success');
  }

  const today = new Date().toISOString().split('T')[0];

  function isOverdue(task) {
    return !task.done && task.due_date && task.due_date < today;
  }

  const countOpen = tasks.filter(t => !t.done).length;
  const countDone = tasks.filter(t => t.done).length;

  return (
    <div style={{ fontFamily: FONT, color: INK }}>

      {/* Header */}
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end',
        marginBottom: isMobile ? '24px' : '40px', gap: '16px',
      }}>
        <div>
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '8px' }}>Aufgaben</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: isMobile ? '26px' : '36px', fontWeight: 900, letterSpacing: '-1.5px', margin: 0 }}>
              Aufgaben & Erinnerungen
            </h1>
            {!loading && (
              <span style={{ fontSize: '13px', color: MUTED }}>
                {countOpen} offen{countDone > 0 ? ` · ${countDone} erledigt` : ''}
              </span>
            )}
          </div>
        </div>
        <button onClick={openNew} style={{
          background: INK, color: 'white', border: 'none',
          padding: '12px 20px', fontSize: '11px', fontWeight: 700,
          letterSpacing: '1px', textTransform: 'uppercase',
          cursor: 'pointer', fontFamily: FONT, whiteSpace: 'nowrap',
        }}>
          + Aufgabe
        </button>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '24px', flexWrap: 'wrap', borderBottom: `1px solid ${BORDER}` }}>
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={() => setCategory(c.key)} style={{
            background: 'none', border: 'none', borderBottom: category === c.key ? `2px solid ${INK}` : '2px solid transparent',
            padding: isMobile ? '10px 12px' : '10px 18px', fontSize: '11px', fontWeight: category === c.key ? 700 : 400,
            color: category === c.key ? INK : MUTED, cursor: 'pointer', fontFamily: FONT,
            letterSpacing: '0.5px', marginBottom: '-1px',
          }}>
            {c.label}
            {c.key !== 'all' && (
              <span style={{ marginLeft: '6px', fontSize: '10px', color: MUTED }}>
                ({tasks.filter(t => t.category === c.key && !t.done).length})
              </span>
            )}
            {c.key === 'all' && (
              <span style={{ marginLeft: '6px', fontSize: '10px', color: MUTED }}>({countOpen})</span>
            )}
          </button>
        ))}
        <button onClick={() => setShowDone(v => !v)} style={{
          background: 'none', border: 'none', borderBottom: showDone ? `2px solid ${BLUE}` : '2px solid transparent',
          padding: isMobile ? '10px 12px' : '10px 18px', fontSize: '11px', fontWeight: showDone ? 700 : 400,
          color: showDone ? BLUE : MUTED, cursor: 'pointer', fontFamily: FONT,
          letterSpacing: '0.5px', marginBottom: '-1px', marginLeft: 'auto',
        }}>
          Erledigt ({countDone})
        </button>
      </div>

      {/* Task list */}
      {loading ? (
        <div style={{ padding: '64px', textAlign: 'center', border: `1px solid ${BORDER}`, background: 'white' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: MUTED }}>Lädt...</span>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '64px', textAlign: 'center', border: `1px solid ${BORDER}`, background: 'white' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: MUTED, marginBottom: '16px' }}>
            Keine Aufgaben
          </div>
          <button onClick={openNew} style={{
            background: INK, color: 'white', border: 'none', padding: '10px 20px',
            fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
            cursor: 'pointer', fontFamily: FONT,
          }}>
            Erste Aufgabe erstellen
          </button>
        </div>
      ) : (
        <div style={{ border: `1px solid ${BORDER}`, background: 'white' }}>
          {filtered.map((task, i) => {
            const overdue = isOverdue(task);
            const pCfg    = PRIORITY_CFG[task.priority] || PRIORITY_CFG.medium;
            const catLabel = CATEGORIES.find(c => c.key === task.category)?.label || task.category;

            return (
              <div key={task.id} style={{
                display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
                gap: '16px', padding: isMobile ? '16px' : '18px 24px',
                borderBottom: i < filtered.length - 1 ? `1px solid ${BORDER}` : 'none',
                opacity: task.done ? 0.5 : 1,
                flexDirection: isMobile ? 'column' : 'row',
              }}>
                {/* Checkbox */}
                <button
                  onClick={() => toggleDone(task)}
                  style={{
                    width: '20px', height: '20px', flexShrink: 0,
                    border: `2px solid ${task.done ? BLUE : BORDER}`,
                    background: task.done ? BLUE : 'white',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0,
                  }}
                >
                  {task.done && <span style={{ color: 'white', fontSize: '11px', lineHeight: 1 }}>✓</span>}
                </button>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <span style={{
                      fontSize: '14px', fontWeight: 700, letterSpacing: '-0.2px',
                      color: task.done ? MUTED : INK,
                      textDecoration: task.done ? 'line-through' : 'none',
                    }}>
                      {task.title}
                    </span>
                    <span style={{
                      fontSize: '9px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                      padding: '2px 6px', border: `1px solid ${BORDER}`, color: pCfg.color,
                    }}>
                      {pCfg.label}
                    </span>
                    <span style={{
                      fontSize: '9px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                      padding: '2px 6px', border: `1px solid ${BORDER}`, color: MUTED,
                    }}>
                      {catLabel}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {task.due_date && (
                      <span style={{ fontSize: '11px', color: overdue ? '#b91c1c' : MUTED, fontWeight: overdue ? 700 : 400 }}>
                        {overdue ? 'Überfällig: ' : 'Fällig: '}
                        {new Date(task.due_date).toLocaleDateString('de-DE')}
                      </span>
                    )}
                    {task.notes && (
                      <span style={{ fontSize: '11px', color: MUTED }}>{task.notes}</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button onClick={() => openEdit(task)} style={{
                    background: 'none', border: `1px solid ${BORDER}`, padding: '6px 12px',
                    fontSize: '10px', color: MUTED, cursor: 'pointer', fontFamily: FONT,
                  }}>
                    Bearbeiten
                  </button>
                  <button
                    onClick={() => handleDelete(task)}
                    disabled={deleting === task.id}
                    style={{
                      background: 'none', border: `1px solid ${BORDER}`, padding: '6px 10px',
                      fontSize: '10px', color: '#b91c1c', cursor: 'pointer', fontFamily: FONT,
                    }}
                  >
                    {deleting === task.id ? '...' : '×'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: isMobile ? 0 : '24px',
          }}
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="immo-modal" style={{ background: 'white', width: '100%', maxWidth: '480px' }}>
            <div style={{ padding: '28px 32px 20px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 900, margin: 0, letterSpacing: '-0.5px' }}>
                {modal === 'new' ? 'Neue Aufgabe' : 'Aufgabe bearbeiten'}
              </h2>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: MUTED, lineHeight: 1 }}>×</button>
            </div>

            <form onSubmit={handleSave} style={{ padding: '28px 32px 32px' }}>
              <Field label="Titel *">
                <input
                  type="text" required value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Was muss erledigt werden?"
                  style={inputStyle} autoFocus
                />
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Field label="Kategorie">
                  <select
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    style={inputStyle}
                  >
                    {CATEGORIES.filter(c => c.key !== 'all').map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Priorität">
                  <select
                    value={form.priority}
                    onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                    style={inputStyle}
                  >
                    {Object.entries(PRIORITY_CFG).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Fälligkeitsdatum">
                <input
                  type="date" value={form.due_date}
                  onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                  style={inputStyle}
                />
              </Field>

              <Field label="Notizen">
                <textarea
                  rows={3} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Optionale Details..."
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </Field>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" disabled={saving} style={{
                  flex: 1, background: saving ? MUTED : INK, color: 'white', border: 'none',
                  padding: '13px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px',
                  textTransform: 'uppercase', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: FONT,
                }}>
                  {saving ? 'Speichert...' : modal === 'new' ? 'Erstellen' : 'Speichern'}
                </button>
                <button type="button" onClick={closeModal} style={{
                  background: 'none', border: `1px solid ${BORDER}`, color: INK,
                  padding: '13px 20px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px',
                  textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT,
                }}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
